// src/Redux/slices/subscriptionSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import APIRequest from "../../API";
import * as APIPath from "../../API/path";
import toast from "react-hot-toast";
import {
  CheckSubscriptionStatusResponse,
  CancelSubscriptionResponse,
  SubscriptionVerificationPayload,
  SubscriptionCancelPayload,
  getSubscriptionId,
} from "../../Config/subscription.types";

const MAX_POLL_ATTEMPTS = 12;
const POLL_INTERVAL = 5000;
const VERIFICATION_TIMEOUT_MESSAGE =
  "Subscription verification is taking longer than expected";
const CANCELLATION_SUBMISSION_FAILED_MESSAGE =
  "Cancellation could not be submitted for this membership. Please contact support.";

interface SubscriptionState {
  verifying: boolean;
  cancelling: boolean;
  error: string | null;
}

const initialState: SubscriptionState = {
  verifying: false,
  cancelling: false,
  error: null,
};
export const verifyAndSubscribe = createAsyncThunk<
  CheckSubscriptionStatusResponse,
  SubscriptionVerificationPayload
>(
  "subscription/verifyAndSubscribe",
  async ({ subscriptionId, creatorId }, { rejectWithValue }) => {
    try {
      let attempts = 0;

      const checkUntilActive =
        async (): Promise<CheckSubscriptionStatusResponse> => {
        attempts += 1;

        if (attempts > MAX_POLL_ATTEMPTS) {
          throw new Error(VERIFICATION_TIMEOUT_MESSAGE);
        }

        // Check subscription status
        const statusResponse = await APIRequest<CheckSubscriptionStatusResponse>(
          APIPath.CheckSubscriptionStatus,
          { subscriptionId }
        );

        if (statusResponse?.error) {
          throw new Error(statusResponse.error.message || "Status check failed");
        }

        if (!statusResponse?.data) {
          throw new Error("Status check failed");
        }

        if (statusResponse?.data?.status === false) {
          const backendError =
            typeof statusResponse.data.error === "string"
              ? statusResponse.data.error
              : statusResponse.data.error?.message;

          throw new Error(
            backendError ||
              statusResponse.data.message ||
              "Status check failed"
          );
        }

        if (!statusResponse?.data?.isActive) {
          // Not ready yet, wait and retry
          await new Promise((resolve) =>
            setTimeout(resolve, POLL_INTERVAL)
          );
          return checkUntilActive();
        }

        return {
          ...statusResponse.data,
          creatorId: statusResponse.data.creatorId || creatorId,
        };
      };

      const result = await checkUntilActive();
      toast.success("Subscription successful!");
      return result;
    } catch (error: any) {
      const message = error.message || "Subscription verification failed";
      if (message === VERIFICATION_TIMEOUT_MESSAGE) {
        toast(
          "Payment received. Subscription activation is still syncing. Please refresh in a moment."
        );
      } else {
        toast.error(message);
      }
      return rejectWithValue(message);
    }
  }
);

/**
 * Cancel subscription
 */
export const cancelSubscriptionThunk = createAsyncThunk<
  CancelSubscriptionResponse,
  SubscriptionCancelPayload
>(
  "subscription/cancel",
  async (payload, { rejectWithValue }) => {
    try {
      const subscription_id =
        getSubscriptionId((payload as any)?.subscribeDetails) ??
        getSubscriptionId(payload as any);
      if (!subscription_id) {
        throw new Error("Missing subscription ID");
      }

      const response = await APIRequest<CancelSubscriptionResponse>(
        APIPath.CancelSubscription,
        {
          subscription_id,
          subscriptionId: subscription_id,
          creatorId: (payload as any)?.creatorId ?? (payload as any)?.creator_id,
        }
      );

      if (response?.error) {
        throw new Error(response.error.message || CANCELLATION_SUBMISSION_FAILED_MESSAGE);
      }

      const submitted =
        response?.data?.data?.providerCancellationSubmitted ??
        response?.data?.providerCancellationSubmitted ??
        response?.data?.status === true;

      if (!submitted) {
        throw new Error(CANCELLATION_SUBMISSION_FAILED_MESSAGE);
      }

      toast.success("Cancellation request submitted. Your membership stays active until the current billing period ends.");
      return {
        ...response.data,
        scheduledCancelAt:
          response?.data?.data?.scheduledCancelAt ??
          response?.data?.scheduledCancelAt ??
          null,
        validUntil:
          response?.data?.data?.validUntil ??
          response?.data?.validUntil ??
          null,
      };
    } catch (error: any) {
      const message = error.message || CANCELLATION_SUBMISSION_FAILED_MESSAGE;
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Verify and Subscribe
    builder.addCase(verifyAndSubscribe.pending, (state) => {
      state.verifying = true;
      state.error = null;
    });
    builder.addCase(verifyAndSubscribe.fulfilled, (state) => {
      state.verifying = false;
      state.error = null;
    });
    builder.addCase(verifyAndSubscribe.rejected, (state, { payload }) => {
      state.verifying = false;
      state.error = (payload as string) || "Verification failed";
    });

    // Cancel subscription
    builder.addCase(cancelSubscriptionThunk.pending, (state) => {
      state.cancelling = true;
      state.error = null;
    });
    builder.addCase(cancelSubscriptionThunk.fulfilled, (state) => {
      state.cancelling = false;
      state.error = null;
    });
    builder.addCase(cancelSubscriptionThunk.rejected, (state, { payload }) => {
      state.cancelling = false;
      state.error = (payload as string) || "Cancellation failed";
    });
  },
});

export const { clearError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
